<template>
  <div class="admin-management rtl pa-3 pa-md-6">
    <template v-if="isManager">
      <div class="page-head d-flex flex-column flex-md-row align-md-center justify-space-between mb-5">
        <div>
          <div class="d-flex align-center mb-2">
            <v-avatar color="secondary" size="46" class="ml-3">
              <v-icon color="white">fa-users-cog</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h6 font-weight-bold secondary--text mb-1">مدیریت ادمین‌ها</h1>
              <p class="grey--text text--darken-1 mb-0 text-body-2">
                ایجاد و مدیریت حساب‌های مدیریتی، نوع پنل، نقش‌ها و وضعیت دسترسی
              </p>
            </div>
          </div>
        </div>

        <v-btn color="secondary" depressed class="rounded-lg white--text mt-3 mt-md-0" @click="openCreateDialog">
          <v-icon small right>fa-user-plus</v-icon>
          ادمین جدید
        </v-btn>
      </div>

      <v-row class="mb-2">
        <v-col cols="12" sm="4">
          <v-card flat class="summary-card rounded-xl pa-4">
            <div class="text-caption grey--text">کل ادمین‌ها</div>
            <div class="text-h5 font-weight-bold secondary--text mt-1">{{ admins.length }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card flat class="summary-card rounded-xl pa-4">
            <div class="text-caption grey--text">فعال</div>
            <div class="text-h5 font-weight-bold success--text mt-1">{{ activeCount }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card flat class="summary-card rounded-xl pa-4">
            <div class="text-caption grey--text">غیرفعال</div>
            <div class="text-h5 font-weight-bold error--text mt-1">{{ admins.length - activeCount }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-card flat class="rounded-xl overflow-hidden admin-table-card">
        <div class="pa-4 d-flex flex-column flex-md-row align-md-center">
          <v-text-field
            v-model="search"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="fa-search"
            label="جستجو در نام، نام کاربری یا نوع پنل"
            class="rounded-lg"
          />
          <v-select
            v-model="typeFilter"
            :items="[{ text: 'همه نوع‌ها', value: null }, ...panelTypes]"
            dense
            outlined
            hide-details
            clearable
            label="نوع پنل"
            class="rounded-lg mr-md-3 mt-3 mt-md-0 type-filter"
          />
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredAdmins"
          :loading="loading"
          :items-per-page="10"
          no-data-text="ادمینی ثبت نشده است"
          loading-text="در حال دریافت ادمین‌ها..."
          class="admin-table"
        >
          <template #item.identity="{ item }">
            <div class="py-2">
              <div class="font-weight-bold">{{ fullName(item) || 'بدون نام' }}</div>
              <div class="text-caption grey--text text--darken-1 ltr d-inline-block">{{ userOf(item).username }}</div>
            </div>
          </template>

          <template #item.user_type="{ item }">
            <v-chip small outlined color="secondary">{{ panelTypeTitle(userOf(item).user_type) }}</v-chip>
          </template>

          <template #item.roles="{ item }">
            <div class="role-list py-2">
              <v-chip v-for="roleId in roleIds(item)" :key="roleId" x-small class="ml-1 mb-1">
                {{ roleTitle(roleId) }}
              </v-chip>
              <span v-if="!roleIds(item).length" class="text-caption grey--text">بدون نقش</span>
            </div>
          </template>

          <template #item.is_active="{ item }">
            <v-switch
              :input-value="isActive(item)"
              :disabled="savingId === item.id || isCurrentUser(item)"
              color="success"
              inset
              hide-details
              class="ma-0 d-inline-flex"
              @change="toggleActive(item, $event)"
            />
          </template>

          <template #item.actions="{ item }">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn icon color="secondary" v-bind="attrs" v-on="on" @click="openEditDialog(item)">
                  <v-icon small>fa-edit</v-icon>
                </v-btn>
              </template>
              ویرایش ادمین
            </v-tooltip>
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn icon color="warning" v-bind="attrs" v-on="on" @click="openPasswordDialog(item)">
                  <v-icon small>fa-key</v-icon>
                </v-btn>
              </template>
              ریست رمز عبور
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>

      <v-dialog v-model="formDialog" max-width="760" persistent>
        <v-card class="rounded-xl">
          <v-card-title class="secondary white--text">
            <v-icon color="white" small class="ml-2">{{ editing ? 'fa-user-edit' : 'fa-user-plus' }}</v-icon>
            {{ editing ? 'ویرایش ادمین' : 'ایجاد ادمین جدید' }}
          </v-card-title>
          <v-card-text class="pt-6">
            <v-form ref="adminForm">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model.trim="form.first_name" outlined dense label="نام" class="rounded-lg" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model.trim="form.last_name" outlined dense label="نام خانوادگی" class="rounded-lg" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="form.username"
                    outlined
                    dense
                    label="* نام کاربری"
                    class="rounded-lg text-field-ltr"
                    :rules="requiredRules"
                    autocomplete="off"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model.trim="form.phone" outlined dense label="شماره موبایل" class="rounded-lg text-field-ltr" />
                </v-col>
                <v-col v-if="!editing" cols="12" md="6">
                  <v-text-field
                    v-model="form.password"
                    outlined
                    dense
                    label="* رمز عبور اولیه"
                    class="rounded-lg text-field-ltr"
                    :type="showCreatePassword ? 'text' : 'password'"
                    :append-icon="showCreatePassword ? 'fa-eye-slash' : 'fa-eye'"
                    :rules="passwordRules"
                    autocomplete="new-password"
                    @click:append="showCreatePassword = !showCreatePassword"
                  />
                </v-col>
                <v-col cols="12" :md="editing ? 12 : 6">
                  <v-select
                    v-model="form.user_type"
                    :items="panelTypes"
                    :disabled="editing && selectedAdmin && isCurrentUser(selectedAdmin)"
                    outlined
                    dense
                    label="* نوع پنل"
                    class="rounded-lg"
                    :rules="requiredRules"
                  />
                </v-col>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="form.roles"
                    :items="roles"
                    item-text="name"
                    item-value="id"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    outlined
                    dense
                    label="نقش‌ها و سطح دسترسی"
                    class="rounded-lg"
                  />
                </v-col>
                <v-col cols="12">
                  <v-switch v-model="form.is_active" inset color="success" label="حساب فعال باشد" hide-details />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions class="px-6 pb-5">
            <v-spacer />
            <v-btn text :disabled="submitting" @click="formDialog = false">انصراف</v-btn>
            <v-btn color="success" depressed class="rounded-lg white--text" :loading="submitting" @click="saveAdmin">
              {{ editing ? 'ثبت تغییرات' : 'ایجاد ادمین' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="passwordDialog" max-width="500" persistent>
        <v-card class="rounded-xl">
          <v-card-title class="warning white--text">
            <v-icon color="white" small class="ml-2">fa-key</v-icon>
            ریست رمز عبور
          </v-card-title>
          <v-card-text class="pt-6">
            <p class="text-body-2 mb-5">
              رمز جدید برای <strong>{{ selectedAdmin ? fullName(selectedAdmin) || userOf(selectedAdmin).username : '' }}</strong>
            </p>
            <v-form ref="passwordForm">
              <v-text-field
                v-model="passwordForm.password"
                outlined
                label="* رمز عبور جدید"
                :type="showResetPassword ? 'text' : 'password'"
                :append-icon="showResetPassword ? 'fa-eye-slash' : 'fa-eye'"
                :rules="passwordRules"
                class="rounded-lg text-field-ltr"
                autocomplete="new-password"
                @click:append="showResetPassword = !showResetPassword"
              />
              <v-text-field
                v-model="passwordForm.repeat"
                outlined
                label="* تکرار رمز عبور جدید"
                :type="showResetPassword ? 'text' : 'password'"
                :rules="passwordRepeatRules"
                class="rounded-lg text-field-ltr"
                autocomplete="new-password"
              />
            </v-form>
          </v-card-text>
          <v-card-actions class="px-6 pb-5">
            <v-spacer />
            <v-btn text :disabled="submitting" @click="passwordDialog = false">انصراف</v-btn>
            <v-btn color="warning" depressed class="rounded-lg white--text" :loading="submitting" @click="resetPassword">
              ثبت رمز جدید
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <v-alert v-else-if="user" type="error" outlined class="rounded-lg">
      این بخش فقط برای مدیر کل قابل دسترسی است.
    </v-alert>
  </div>
</template>

<script>
import getRolesApi from "../role/getRolesApi";

export default {
  name: "AdminManagement",
  mixins: [getRolesApi],
  data() {
    return {
      loading: false,
      submitting: false,
      savingId: null,
      search: "",
      typeFilter: null,
      admins: [],
      roles: [],
      formDialog: false,
      passwordDialog: false,
      editing: false,
      selectedAdmin: null,
      showCreatePassword: false,
      showResetPassword: false,
      panelTypes: [
        { text: "مدیر کل", value: "ma" },
        { text: "ادمین پردازش و بسته‌بندی", value: "paad" },
        { text: "انباردار", value: "stke" },
        { text: "تولیدکننده محتوا", value: "so" },
        { text: "ادمین فروش دستی", value: "saad" },
        { text: "صاحب کسب‌وکار", value: "buow" },
        { text: "ادمین خروج کالا", value: "prdead" },
        { text: "ادمین تأمین‌کننده", value: "suad" },
        { text: "شریک", value: "prtnr" },
        { text: "صندوق‌دار", value: "cshr" },
        { text: "پشتیبانی", value: "suprt" },
        { text: "بازاریابی و افزایش فروش", value: "mkt" },
      ],
      headers: [
        { text: "کاربر", value: "identity" },
        { text: "نوع پنل", value: "user_type", sortable: false },
        { text: "نقش‌ها", value: "roles", sortable: false },
        { text: "فعال", value: "is_active", sortable: false, align: "center" },
        { text: "عملیات", value: "actions", sortable: false, align: "center" },
      ],
      form: {},
      passwordForm: { password: "", repeat: "" },
      requiredRules: [(value) => !!value || "این فیلد اجباری است"],
    };
  },
  computed: {
    isManager() {
      return !!(this.user && (this.user.user_type === "ma" || this.user.is_superuser));
    },
    activeCount() {
      return this.admins.filter((item) => this.isActive(item)).length;
    },
    filteredAdmins() {
      const needle = (this.search || "").trim().toLowerCase();
      return this.admins.filter((item) => {
        const user = this.userOf(item);
        const matchesType = !this.typeFilter || user.user_type === this.typeFilter;
        const haystack = [
          user.first_name,
          user.last_name,
          user.username,
          user.mobile_number || user.phone,
          this.panelTypeTitle(user.user_type),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return matchesType && (!needle || haystack.includes(needle));
      });
    },
    passwordRules() {
      return [
        (value) => !!value || "رمز عبور اجباری است",
        (value) => (value && value.length >= 8) || "رمز عبور باید حداقل ۸ کاراکتر باشد",
      ];
    },
    passwordRepeatRules() {
      return [
        (value) => !!value || "تکرار رمز عبور اجباری است",
        (value) => value === this.passwordForm.password || "رمز عبور و تکرار آن یکسان نیستند",
      ];
    },
  },
  watch: {
    user(value) {
      this.initializeForUser(value);
    },
  },
  created() {
    // Global mixins read the token before this component hook runs. Starting
    // requests here avoids the token-less 401 caused by an immediate watcher.
    this.initializeForUser(this.user);
  },
  mounted() {
    this.setDocumentTitle("مدیریت ادمین‌ها");
  },
  methods: {
    initializeForUser(value) {
      if (!value) return;
      if (!this.isManager) {
        this.notify("این بخش فقط برای مدیر کل قابل دسترسی است", "danger");
        this.$router.replace({ name: "Home" });
        return;
      }
      if (!this.admins.length && !this.loading) this.loadData();
    },
    emptyForm() {
      return {
        membershipId: null,
        userId: null,
        first_name: "",
        last_name: "",
        username: "",
        phone: "",
        password: "",
        user_type: null,
        roles: [],
        is_active: true,
      };
    },
    loadData() {
      this.loading = true;
      this.getRoles((data) => (this.roles = data || []));
      this.request({
        url: this.endpoint("users/admins/"),
        method: "get",
        success: (data) => {
          this.admins = Array.isArray(data) ? data : data.results || [];
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.handleError(error, {});
        },
      });
    },
    userOf(item) {
      return item && item.user ? item.user : item || {};
    },
    fullName(item) {
      const user = this.userOf(item);
      return [user.first_name, user.last_name].filter(Boolean).join(" ");
    },
    isActive(item) {
      return this.userOf(item).is_active !== false;
    },
    isCurrentUser(item) {
      return !!(this.user && this.userOf(item).id === this.user.id);
    },
    roleIds(item) {
      return (this.userOf(item).roles || []).map((role) => (typeof role === "object" ? role.id : role));
    },
    roleTitle(roleId) {
      const role = this.roles.find((item) => item.id === roleId);
      return role ? role.name : `نقش ${roleId}`;
    },
    panelTypeTitle(value) {
      const type = this.panelTypes.find((item) => item.value === value);
      return type ? type.text : value || "تعیین نشده";
    },
    openCreateDialog() {
      this.editing = false;
      this.selectedAdmin = null;
      this.form = this.emptyForm();
      this.showCreatePassword = false;
      this.formDialog = true;
      this.$nextTick(() => this.$refs.adminForm && this.$refs.adminForm.resetValidation());
    },
    openEditDialog(item) {
      const user = this.userOf(item);
      this.editing = true;
      this.selectedAdmin = item;
      this.form = {
        ...this.emptyForm(),
        userId: user.id,
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        username: user.username || "",
        phone: user.mobile_number || user.phone || "",
        user_type: user.user_type || null,
        roles: this.roleIds(item),
        is_active: this.isActive(item),
      };
      this.formDialog = true;
      this.$nextTick(() => this.$refs.adminForm && this.$refs.adminForm.resetValidation());
    },
    userPayload(includePassword = false) {
      const payload = {
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        username: this.form.username,
        mobile_number: this.form.phone || null,
        user_type: this.form.user_type,
        is_active: this.form.is_active,
        roles: this.form.roles,
      };
      if (includePassword) payload.password = this.form.password;
      return payload;
    },
    saveAdmin() {
      if (!this.$refs.adminForm.validate()) return;
      this.submitting = true;
      if (this.editing) this.updateAdmin();
      else this.createAdmin();
    },
    createAdmin() {
      this.request({
        url: this.endpoint("users/admins/"),
        method: "post",
        data: this.userPayload(true),
        success: () => this.finishSave("ادمین جدید با موفقیت ایجاد شد"),
        error: (error) => this.finishError(error),
      });
    },
    updateAdmin() {
      this.request({
        url: this.endpoint(`users/admins/${this.form.userId}/`),
        method: "patch",
        data: this.userPayload(false),
        success: () => this.finishSave("اطلاعات ادمین با موفقیت ویرایش شد"),
        error: (error) => this.finishError(error),
      });
    },
    finishSave(message) {
      this.submitting = false;
      this.formDialog = false;
      this.passwordDialog = false;
      this.successNotify(message);
      this.loadData();
    },
    finishError(error) {
      this.submitting = false;
      this.handleError(error, {});
    },
    toggleActive(item, value) {
      if (this.isCurrentUser(item)) {
        this.notify("نمی‌توانید حساب کاربری خودتان را غیرفعال کنید", "warning");
        return;
      }
      this.savingId = item.id;
      this.request({
        url: this.endpoint(`users/admins/${this.userOf(item).id}/`),
        method: "patch",
        data: { is_active: value },
        success: () => {
          this.savingId = null;
          item.is_active = value;
          this.successNotify(value ? "حساب ادمین فعال شد" : "حساب ادمین غیرفعال شد");
        },
        error: (error) => {
          this.savingId = null;
          this.finishError(error);
          this.loadData();
        },
      });
    },
    openPasswordDialog(item) {
      this.selectedAdmin = item;
      this.passwordForm = { password: "", repeat: "" };
      this.showResetPassword = false;
      this.passwordDialog = true;
      this.$nextTick(() => this.$refs.passwordForm && this.$refs.passwordForm.resetValidation());
    },
    resetPassword() {
      if (!this.$refs.passwordForm.validate()) return;
      this.submitting = true;
      const userId = this.userOf(this.selectedAdmin).id;
      this.request({
        url: this.endpoint(`users/admins/${userId}/reset-password/`),
        method: "post",
        data: { new_password: this.passwordForm.password },
        success: () => this.finishSave("رمز عبور ادمین با موفقیت تغییر کرد"),
        error: (error) => this.finishError(error),
      });
    },
  },
};
</script>

<style scoped>
.admin-management {
  min-height: calc(100vh - 120px);
  background: #f7f9fc;
}

.page-head,
.summary-card,
.admin-table-card {
  border: 1px solid #e8edf4 !important;
}

.page-head {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
}

.summary-card {
  background: linear-gradient(145deg, #fff, #f8fafc);
}

.type-filter {
  max-width: 260px;
}

.role-list {
  max-width: 320px;
}

.ltr {
  direction: ltr;
}

@media (max-width: 600px) {
  .type-filter {
    max-width: none;
  }
}
</style>
